import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Crown, TrendingUp, Globe, Users, Sparkles, ArrowRight, Zap, RefreshCw, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import PricingModal from '@/components/PricingModal';
import RegionalRatings from '@/components/RegionalRatings';
import DemographicInsights from '@/components/DemographicInsights';
import FacialAnalysis from '@/components/FacialAnalysis';
import EthnicityAnalysis from '@/components/EthnicityAnalysis';
import VenueAnalysis from '@/components/VenueAnalysis';
import Footer from '@/components/Footer';
import { generateRatings } from '@/utils/ratingGenerator';

const Results = () => {
  const navigate = useNavigate();
  const [showPricing, setShowPricing] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [ratings, setRatings] = useState<any>(null);

  useEffect(() => {
    const image = localStorage.getItem('uploadedImage');
    const premium = localStorage.getItem('isPremium') === 'true';
    
    if (!image) {
      navigate('/');
      return;
    }
    
    setUploadedImage(image);
    setIsPremium(premium);
    
    // Generate ratings based on image
    const generatedRatings = generateRatings(image);
    setRatings(generatedRatings);
    
    // Store ratings for consistency
    localStorage.setItem('userRatings', JSON.stringify(generatedRatings));
  }, [navigate]);

  if (!ratings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="h-16 w-16 text-purple-600 mx-auto mb-4 animate-spin" />
          <p className="text-xl text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-purple-600" />
            <h1 className="text-2xl font-bold text-gradient">AttractivenessAI</h1>
          </div>
          <div className="flex items-center gap-4">
            {isPremium ? (
              <div className="flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 px-4 py-2 rounded-full shadow-md">
                <Crown className="h-5 w-5 text-purple-600" />
                <span className="font-semibold text-purple-600">Premium Member</span>
              </div>
            ) : (
              <Button 
                className="gradient-primary text-white hover:opacity-90 shadow-lg hover:shadow-xl transition-all"
                onClick={() => setShowPricing(true)}
              >
                <Crown className="h-4 w-4 mr-2" />
                Unlock Full Analysis
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="shadow-sm">
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {/* Overall Score Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <Card className="p-8 bg-white/90 backdrop-blur-sm shadow-2xl border-2 border-purple-100">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              {uploadedImage && (
                <div className="relative">
                  <img 
                    src={uploadedImage} 
                    alt="Your photo" 
                    className="w-48 h-48 rounded-2xl object-cover shadow-xl ring-4 ring-purple-200"
                  />
                  <div className="absolute -bottom-3 -right-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full shadow-lg font-bold">
                    {ratings.overallScore}/10
                  </div>
                </div>
              )}
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-4xl font-bold mb-4 text-gray-800">Your Attractiveness Score</h2>
                <div className="flex items-end gap-2 justify-center md:justify-start mb-4">
                  <span className="text-7xl font-bold text-gradient">{ratings.overallScore}</span>
                  <span className="text-4xl text-gray-400 mb-2">/10</span>
                </div>
                <p className="text-2xl text-gray-700 mb-3">
                  You rank in the <span className="font-bold text-purple-600">{ratings.globalRank}</span> globally
                </p>
                <p className="text-gray-600 mb-4">
                  Based on AI analysis of facial symmetry, features, and global beauty standards
                </p>
                {!isPremium && (
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-400 rounded-full px-5 py-3 text-sm shadow-lg">
                      <Zap className="h-5 w-5 text-yellow-600" />
                      <span className="text-yellow-900 font-bold">
                        Unlock complete breakdown for just $9.99!
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Venue Analysis - NEW */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-3xl font-bold flex items-center gap-2">
              <MapPin className="h-7 w-7 text-green-600" />
              Venue Attractiveness Analysis
            </h3>
            {!isPremium && (
              <Button 
                variant="outline" 
                className="text-purple-600 border-2 border-purple-300 hover:bg-purple-50 shadow-md"
                onClick={() => setShowPricing(true)}
              >
                <Lock className="h-4 w-4 mr-2" />
                Unlock All Venues
              </Button>
            )}
          </div>
          
          <VenueAnalysis 
            isPremium={isPremium} 
            onUpgrade={() => setShowPricing(true)}
            venueScores={ratings.venueScores}
          />
        </motion.div>

        {/* Ethnicity & Cultural Analysis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-3xl font-bold flex items-center gap-2">
              <Globe className="h-7 w-7 text-blue-600" />
              Ethnicity & Cultural Analysis
            </h3>
            {!isPremium && (
              <Button 
                variant="outline" 
                className="text-purple-600 border-2 border-purple-300 hover:bg-purple-50 shadow-md"
                onClick={() => setShowPricing(true)}
              >
                <Lock className="h-4 w-4 mr-2" />
                Unlock Analysis
              </Button>
            )}
          </div>
          
          <EthnicityAnalysis 
            isPremium={isPremium} 
            onUpgrade={() => setShowPricing(true)}
            ethnicityData={ratings.ethnicity}
          />
        </motion.div>

        {/* Free Preview - Regional Ratings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-3xl font-bold flex items-center gap-2">
              <Globe className="h-7 w-7 text-purple-600" />
              Regional Attractiveness Ratings
            </h3>
            {!isPremium && (
              <Button 
                variant="outline" 
                className="text-purple-600 border-2 border-purple-300 hover:bg-purple-50 shadow-md"
                onClick={() => setShowPricing(true)}
              >
                <Lock className="h-4 w-4 mr-2" />
                Unlock All 50+ Cities
              </Button>
            )}
          </div>
          
          <RegionalRatings 
            isPremium={isPremium} 
            onUpgrade={() => setShowPricing(true)}
            regionalScores={ratings.regionalScores}
          />
        </motion.div>

        {/* Demographic Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-7 w-7 text-pink-600" />
              Demographic Preferences & Attraction Tips
            </h3>
            {!isPremium && (
              <Button 
                variant="outline" 
                className="text-purple-600 border-2 border-purple-300 hover:bg-purple-50 shadow-md"
                onClick={() => setShowPricing(true)}
              >
                <Lock className="h-4 w-4 mr-2" />
                See Full Analysis
              </Button>
            )}
          </div>
          
          <DemographicInsights 
            isPremium={isPremium} 
            onUpgrade={() => setShowPricing(true)}
            demographicScores={ratings.demographicScores}
          />
        </motion.div>

        {/* Facial Analysis (Premium Only) */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <h3 className="text-3xl font-bold flex items-center gap-2 mb-6">
              <Sparkles className="h-7 w-7 text-purple-600" />
              Advanced Facial Analysis
            </h3>
            <FacialAnalysis 
              isPremium={isPremium}
              facialFeatures={ratings.facialFeatures}
            />
          </motion.div>
        )}

        {/* Premium Upsell Banner */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-4xl mx-auto"
          >
            <Card className="p-10 gradient-primary text-white relative overflow-hidden shadow-2xl border-4 border-purple-300">
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <Crown className="h-16 w-16" />
                  <div>
                    <h3 className="text-4xl font-bold mb-2">
                      🔥 Unlock Your Complete Analysis
                    </h3>
                    <p className="text-xl opacity-90">
                      Limited Time: <span className="font-bold">80% OFF</span> - Just $9.99!
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                      <Zap className="h-6 w-6" />
                      One-Time Payment ($9.99)
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>📍 16 venue types analyzed (gym, club, park, etc.)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>🎯 Specific strategies for each venue</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>🌍 Predicted ethnicity & country of origin</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>✅ 8+ countries where you'll blend in</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>⚠️ Honest analysis of challenging locations</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>50+ cities worldwide ratings</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>Complete demographic breakdown</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>Lifetime access - no recurring fees!</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                      <RefreshCw className="h-6 w-6" />
                      Monthly Pro ($15/month)
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>Everything in One-Time, PLUS:</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span><strong>NEW advice every month</strong></span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>Target specific demographics</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>Monthly progress tracking</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        <span>Updated venue strategies</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg"
                    className="bg-white text-purple-600 hover:bg-gray-100 flex-1 h-14 text-lg font-bold shadow-xl"
                    onClick={() => setShowPricing(true)}
                  >
                    <Zap className="h-6 w-6 mr-2" />
                    Get Instant Access - $9.99
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 flex-1 h-14 text-lg font-bold"
                    onClick={() => setShowPricing(true)}
                  >
                    <RefreshCw className="h-6 w-6 mr-2" />
                    Monthly Pro - $15/mo
                  </Button>
                </div>
                <p className="text-center text-sm mt-4 opacity-90">
                  ⚡ 30-day money-back guarantee • Secure payment • Instant access
                </p>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />
              <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
            </Card>
          </motion.div>
        )}
      </div>

      <Footer />

      <PricingModal 
        open={showPricing} 
        onClose={() => setShowPricing(false)}
        onUpgrade={() => {
          setIsPremium(true);
          localStorage.setItem('isPremium', 'true');
          setShowPricing(false);
        }}
      />
    </div>
  );
};

export default Results;